import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { adminAPI } from '@/lib/api';
import { Shield, CheckCircle, XCircle, Clock, Eye, FileText } from 'lucide-react';
import { toast } from 'sonner';

const statusColors = {
  pending: 'bg-amber-500/15 text-amber-400',
  approved: 'bg-emerald-500/15 text-emerald-400',
  rejected: 'bg-red-500/15 text-red-400',
};

export default function AdminKYCPage() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selected, setSelected] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewing, setReviewing] = useState(false);

  const fetchKYC = async () => {
    setLoading(true);
    try {
      const { data } = await adminAPI.getKYC(filter);
      setSubmissions(data);
    } catch { toast.error('Failed to load KYC submissions'); }
    setLoading(false);
  };

  useEffect(() => { fetchKYC(); }, [filter]); // eslint-disable-line

  const handleReview = async (kycId, status) => {
    setReviewing(true);
    try {
      await adminAPI.reviewKYC(kycId, { status, notes: reviewNotes });
      toast.success(`KYC ${status}`);
      setSelected(null);
      setReviewNotes('');
      fetchKYC();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Review failed');
    }
    setReviewing(false);
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-kyc-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/15 flex items-center justify-center">
              <Shield className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h1 className="text-lg font-semibold font-['Outfit'] text-[var(--text-primary)]">KYC Verification</h1>
              <p className="text-xs text-[var(--text-secondary)]">Review user identity verification submissions</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2" data-testid="kyc-filter-tabs">
          {['pending', 'approved', 'rejected', 'all'].map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`kyc-filter-${s}`}
              className={`px-4 py-2 rounded text-sm font-medium transition-all ${filter === s ? 'bg-green-500/15 text-green-400 border border-green-500/30' : 'bg-[var(--bg-surface)] text-[var(--text-secondary)] border border-[var(--border-subtle)] hover:text-[var(--text-primary)]'}`}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-sm text-[var(--text-secondary)]">Loading...</div>
          ) : submissions.length === 0 ? (
            <div className="p-8 text-center text-sm text-[var(--text-secondary)]">No {filter} KYC submissions</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-[var(--text-secondary)] uppercase border-b border-[var(--border-subtle)]">
                  <th className="text-left px-5 py-3">User</th>
                  <th className="text-left px-3 py-3">Full Name</th>
                  <th className="text-left px-3 py-3">ID Type</th>
                  <th className="text-left px-3 py-3">Status</th>
                  <th className="text-left px-3 py-3">Submitted</th>
                  <th className="text-right px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {submissions.map((s) => (
                  <tr key={s.id} className="border-b border-[var(--border-subtle)] hover:bg-[var(--hover-bg)]">
                    <td className="px-5 py-3">
                      <p className="text-[var(--text-primary)] font-medium">{s.user_name}</p>
                      <p className="text-xs text-[var(--text-secondary)]">{s.user_email}</p>
                    </td>
                    <td className="px-3 py-3 text-[var(--text-primary)]">{s.full_name}</td>
                    <td className="px-3 py-3 text-[var(--text-secondary)] capitalize">{s.id_type}</td>
                    <td className="px-3 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColors[s.status] || ''}`}>{s.status}</span>
                    </td>
                    <td className="px-3 py-3 text-xs text-[var(--text-secondary)]">{new Date(s.submitted_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' })}</td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => setSelected(s)} data-testid={`kyc-view-${s.id}`}
                        className="p-1.5 rounded hover:bg-[var(--hover-bg)] text-[var(--text-secondary)] hover:text-green-400 transition-colors">
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Review Modal */}
        {selected && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="kyc-review-modal">
            <div className="w-full max-w-lg bg-[var(--bg-elevated)] border border-[var(--border-subtle)] rounded-lg p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold font-['Outfit'] text-[var(--text-primary)] flex items-center gap-2">
                  <FileText className="w-4 h-4 text-green-400" /> KYC Review
                </h2>
                <button onClick={() => { setSelected(null); setReviewNotes(''); }} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 bg-[var(--bg-primary)] rounded border border-[var(--border-subtle)]">
                  <span className="text-xs text-[var(--text-secondary)]">Full Name</span>
                  <p className="text-[var(--text-primary)] font-medium mt-0.5">{selected.full_name}</p>
                </div>
                <div className="p-3 bg-[var(--bg-primary)] rounded border border-[var(--border-subtle)]">
                  <span className="text-xs text-[var(--text-secondary)]">Date of Birth</span>
                  <p className="text-[var(--text-primary)] font-medium mt-0.5">{selected.date_of_birth}</p>
                </div>
                <div className="p-3 bg-[var(--bg-primary)] rounded border border-[var(--border-subtle)]">
                  <span className="text-xs text-[var(--text-secondary)]">ID Type</span>
                  <p className="text-[var(--text-primary)] font-medium mt-0.5 capitalize">{selected.id_type}</p>
                </div>
                <div className="p-3 bg-[var(--bg-primary)] rounded border border-[var(--border-subtle)]">
                  <span className="text-xs text-[var(--text-secondary)]">ID Number</span>
                  <p className="text-[var(--text-primary)] font-mono font-medium mt-0.5">{selected.id_number}</p>
                </div>
                <div className="col-span-2 p-3 bg-[var(--bg-primary)] rounded border border-[var(--border-subtle)]">
                  <span className="text-xs text-[var(--text-secondary)]">Address</span>
                  <p className="text-[var(--text-primary)] mt-0.5">{selected.address}</p>
                </div>
                {selected.id_image_url && (
                  <div className="col-span-2 p-3 bg-[var(--bg-primary)] rounded border border-[var(--border-subtle)]">
                    <span className="text-xs text-[var(--text-secondary)] block mb-2">ID Document Image</span>
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${selected.id_image_url}`} alt="ID Document"
                      data-testid="kyc-id-image-preview"
                      className="w-full max-h-48 object-contain rounded border border-[var(--border-subtle)]" />
                  </div>
                )}
              </div>

              {selected.status === 'pending' && (
                <>
                  <div>
                    <label className="text-xs text-[var(--text-secondary)] mb-1 block">Review Notes (optional)</label>
                    <textarea value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} data-testid="kyc-review-notes"
                      placeholder="Add notes for the user..."
                      className="w-full h-20 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded p-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] resize-none focus:border-green-500 outline-none" />
                  </div>
                  <div className="flex gap-3 justify-end">
                    <button onClick={() => handleReview(selected.id, 'rejected')} disabled={reviewing} data-testid="kyc-reject-btn"
                      className="px-5 py-2 bg-red-500/15 text-red-400 hover:bg-red-500/25 rounded text-sm font-medium transition-all disabled:opacity-50">
                      Reject
                    </button>
                    <button onClick={() => handleReview(selected.id, 'approved')} disabled={reviewing} data-testid="kyc-approve-btn"
                      className="px-5 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded text-sm font-medium transition-all disabled:opacity-50">
                      Approve
                    </button>
                  </div>
                </>
              )}
              {selected.status !== 'pending' && (
                <div className={`p-3 rounded text-sm ${statusColors[selected.status]}`}>
                  Status: <span className="font-medium capitalize">{selected.status}</span>
                  {selected.notes && <p className="text-xs mt-1 opacity-80">Notes: {selected.notes}</p>}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
